<template>
  <v-dialog
    v-model="dialog"
    scrollable
    max-width="90vw"
    @click:outside="discardItem"
  >
    <v-card>
      <v-card-title class="d-flex align-start justify-space-between">
        <p>{{ title }}</p>
        <v-btn icon @click="discardItem">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="better-scrollbar">
        <v-container fluid>
          <v-row no-gutters>
            <v-col cols="12" md="6" class="pa-2">
              <v-text-field
                v-model="object.name"
                :label="$t('zones.name')"
                :disabled="!isEditting || object.isExternal"
                outlined
                :error="hasErrors('name')"
                :error-messages="getErrors('name')"
              />
            </v-col>
            <v-col cols="12" md="6" class="pa-2">
              <v-select
                v-model="object.type"
                :items="types"
                :label="$t('zones.type.title')"
                :disabled="!isEditting || object.isExternal"
                outlined
                :error="hasErrors('type')"
                :error-messages="getErrors('type')"
              />
            </v-col>
            <v-col cols="12" class="pa-2">
              <v-btn
                v-if="isEditting && !object.isExternal"
                text
                color="orange darken-2 white--text"
                @click="drawPolygon()"
              >
                <v-icon class="mr-2">mdi-pencil</v-icon>
                {{ $t('actions.map.edit') }}
              </v-btn>
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col cols="12" class="pa-2">
              <v-card height="50vh" color="green lighten-3" elevation="0">
                <gm-map
                  ref="zonesMap"
                  v-model="object"
                  :show="dialog"
                  :disabled="!isEditting"
                  :dark="isDark"
                  map-id="zoneMap"
                  class="green lighten-3"
                  width="100%"
                  height="100%"
                />
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>

      <v-card-actions class="d-flex justify-space-between">
        <v-btn
          v-if="isEditting"
          :ripple="false"
          outlined
          dark
          color="primary"
          @click="discardItem"
        >
          {{ $t('actions.discard') }}
        </v-btn>
        <v-btn
          v-if="isEditting && !object.isExternal"
          :ripple="false"
          depressed
          color="primary"
          @click="saveItem(object.id)"
        >
          {{ $t('actions.save') }}
        </v-btn>
        <v-btn
          v-if="!isEditting && !object.isExternal"
          :ripple="false"
          depressed
          color="orange darken-1 white--text"
          @click="isEditting = true"
        >
          {{ $t('actions.edit') }}
        </v-btn>
      </v-card-actions>
      <v-card-actions class="d-flex justify-space-between">
        <v-btn
          v-if="!isEditting && object.isExternal"
          :ripple="false"
          outlined
          dark
          color="primary"
          @click="discardItem"
        >
          {{ $t('actions.back') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState } from 'vuex'
import { orm } from '@/mixins'
import addMutation from '@/graphql/mutations/zones/add.gql'
import editMutation from '@/graphql/mutations/zones/edit.gql'
import Maps from './Maps.vue'

export default {
  components: {
    'gm-map': Maps
  },

  mixins: [orm],
  model: {
    prop: 'data',
    event: 'set-object'
  },

  props: {
    showDialog: { type: Boolean, default: () => false },
    data: { type: Object, default: () => {} },
    readonly: {
      type: Boolean,
      default: () => false
    }
  },

  data () {
    return {
      objectMap: {},
      show: false,
      errors: {}
    }
  },

  computed: {
    ...mapState(['isDark', 'users', 'zones']),
    object: {
      get () {
        return this.data
      },

      set (value) {
        this.$emit('toggle-object', value)
      }
    },

    dialog: {
      get () {
        return this.showDialog
      },

      set (value) {
        this.$emit('toggle-dialog', value)
      }
    },

    types () {
      return [
        { value: 'GEOFENCE', text: this.$i18n.t('zones.type.geofence') },
        { value: 'RESTRICTED', text: this.$i18n.t('zones.type.restricted') },
        { value: 'DANGEROUS', text: this.$i18n.t('zones.type.dangerous') }
      ]
    },

    isEditting: {
      get () {
        return !this.readonly
      },

      set (value) {
        this.$emit('set-readonly', value)
      }
    },

    title () {
      return this.object.id !== undefined ? this.object.name : this.$i18n.t('zones.new')
    },

    objectType () {
      return this.object.type
    }
  },

  watch: {
    objectType () {
      this.setColor()
    },

    isEditting (v) {
      setTimeout(() => {
        if (v && this.dialog) {
          this.setPolygon()
        }
      }, 0)
    },

    dialog (v) {
      setTimeout(() => {
        if (v && this.isEditting) {
          this.setPolygon()
        }
      }, 0)
    }
  },

  created () {},

  methods: {
    async saveItem (id) {
      this.isLoading = true
      const data = {
        id: id,
        name: this.object.name,
        type: this.object.type,
        points: this.object.points
      }
      data.points.forEach(point => { delete point.__typename })
      console.log(data)
      if (id) {
        await this.edit(data)
      } else {
        await this.add(data)
      }
    },

    evaluateResponse ({ status, errors, result, action }) {
      this.isLoading = false
      console.log(status, errors)
      switch (status) {
        case 'OK':
          this.discardItem()
          this.resetErrors()
          this.$store.commit('toggleSnackbar', {
            color: 'green darken-2',
            message: this.$t('helpers.save.success')
          })
          if (action === 'add' || action === 'edit') {
            this.$store.dispatch('zones/getList', true)
          }
          break
        case 'UNPROCESSABLE':
          this.errors = this.parseErrors(errors)
          this.$store.commit('toggleSnackbar', {
            color: 'orange darken-2',
            message: this.$t('errors.invalidFields')
          })
          break
        case 'BADREQUEST':
          this.errors = this.parseErrors(errors)
          this.$store.commit('toggleSnackbar', {
            color: 'orange darken-2',
            message: this.$t('errors.invalidFields')
          })
          break
        case 'LIMITREACHED':
          this.$store.commit('toggleSnackbar', {
            color: 'orange darken-2',
            message: this.$t('errors.limit_reached')
          })
          break
      }
    },

    add (data) {
      const action = 'add'
      this.$apollo.mutate({
        mutation: addMutation,
        variables: {
          token: this.users.entity.token,
          data
        },
        fetchPolicy: 'no-cache'
      }).then(response => {
        const { status, errors, result } = response.data.addZone
        this.evaluateResponse({ status, errors, result, action })
      }).catch(err => {
        console.log('Error handled', err)
        this.$store.commit('toggleSnackbar', {
          color: 'red darken-2',
          message: this.$t('helpers.errors.disaster')
        })
      })
    },

    edit (data) {
      const action = 'edit'
      this.$apollo.mutate({
        mutation: editMutation,
        variables: {
          token: this.users.entity.token,
          data
        },
        fetchPolicy: 'no-cache'
      }).then(response => {
        const { status, errors, result } = response.data.editZone
        this.evaluateResponse({ status, errors, result, action })
      }).catch(err => {
        console.log('Error handled', err)
        this.$store.commit('toggleSnackbar', {
          color: 'red darken-2',
          message: this.$t('helpers.errors.disaster')
        })
      })
    },

    discardItem () {
      this.dialog = false
      this.object = Object.assign({}, this.$store.getters['zones/defaultObject'])
      this.resetErrors()
      setTimeout(() => {
        this.$refs.zonesMap.cleanPolygons()
      }, 0)
    },

    drawPolygon () {
      setTimeout(() => {
        this.$refs.zonesMap.drawPolygon()
      }, 0)
    },

    cleanPolygons () {
      setTimeout(() => {
        this.$refs.zonesMap.cleanPolygons()
      }, 0)
    },

    setColor () {
      setTimeout(() => {
        this.$refs.zonesMap.setColor()
      }, 0)
    },

    setPolygon () {
      setTimeout(() => {
        this.$refs.zonesMap.setPolygon()
      }, 0)
    }
  }
}
</script>
